<script setup>
import { useI18n } from 'vue-i18n'
import CatalogPlaceholder from '@/Components/Public/CatalogPlaceholder.vue'

// Shared public product card — used by the home ProductRail and the
// Rentals/Tours/Property listing grids so the card looks identical
// everywhere. The parent controls sizing (rail = fixed width flex item,
// grid = full cell); this component owns the card's look + content.
// Accent for hover/price comes from --card-accent (defaults to brand coral).
const props = defineProps({
  kind: { type: String, required: true }, // vehicle | tour | property | transfer | package | charter
  item: { type: Object, required: true },
  href: { type: String, required: true },
})

const { t } = useI18n()

// Transfer routes have no duration ⇒ minutes formatting mirrors the Show
// page's own helper (kept in sync manually — small enough not to share).
function formatDuration(mins) {
  if (!mins) return ''
  if (mins < 60) return t('public.transfers.duration_min', { n: mins })
  const h = Math.floor(mins / 60)
  const m = mins % 60
  return m > 0 ? t('public.transfers.duration_hm', { h, m }) : t('public.transfers.duration_h', { h })
}

function title() {
  if (props.kind === 'vehicle')  return `${props.item.make} ${props.item.model}`.trim()
  if (props.kind === 'transfer') return props.item.route_label
  return props.item.title
}
function meta() {
  const it = props.item
  if (props.kind === 'vehicle') {
    const parts = []
    if (it.vehicle_class === 'two_wheeler') { if (it.engine_cc) parts.push(`${it.engine_cc}cc`) }
    else if (it.seats) parts.push(t('public.catalog.seats_count', { n: it.seats }))
    if (it.transmission) parts.push(t(`public.transmissions.${it.transmission}`))
    if (it.fuel_type)    parts.push(t(`public.fuels.${it.fuel_type}`))
    return parts.join(' · ')
  }
  if (props.kind === 'tour') {
    // public.experiences.categories (not vendor_portal.tour.categories,
    // which only ships en/ru) — the vendor-portal key was falling back to
    // English on zh/th (I-1, fix-wave 2026-07-30).
    const parts = [t(`public.experiences.categories.${it.category}`)]
    if (it.duration_minutes) parts.push(`${Math.round(it.duration_minutes / 60)}h`)
    return parts.join(' · ')
  }
  if (props.kind === 'transfer') {
    const parts = []
    if (it.duration_minutes) parts.push(formatDuration(it.duration_minutes))
    if (it.bidirectional) parts.push(t('public.transfers.bidirectional_note'))
    return parts.join(' · ')
  }
  if (props.kind === 'package') return t(`public.packages.kind.${it.kind}`)
  if (props.kind === 'charter') {
    const parts = [t(`public.charters.vessel_types.${it.vessel_type}`)]
    if (it.length_ft) parts.push(t('public.charters.ft', { n: it.length_ft }))
    if (it.capacity)  parts.push(t('public.charters.up_to', { n: it.capacity }))
    return parts.join(' · ')
  }
  const parts = [t(`host_portal.property.categories.${it.category}`)]
  if (it.area) parts.push(it.area)
  return parts.join(' · ')
}
function price() {
  if (props.kind === 'vehicle')  return props.item.daily_rate
  // card_price (not price_adult) — a per_session experience prices flat
  // per session, so price_adult stays a formatted ฿0.00 (C-1, fix-wave
  // 2026-07-30).
  if (props.kind === 'tour')     return props.item.card_price
  if (props.kind === 'transfer') return transferFromPrice()
  if (props.kind === 'package')  return props.item.price
  if (props.kind === 'charter')  return props.item.from_price ? `${t('public.charters.from')} ${props.item.from_price}` : ''
  return props.item.price_per_night || props.item.price_per_month
}

function tourPriceUnit() {
  if (props.item.pricing_model !== 'per_session') return t('public.experiences.units.per_adult')
  // a private/VIP experience priced per_session is one whole group per booking
  return ['private', 'vip'].includes(props.item.tour_type)
    ? t('public.experiences.units.per_group')
    : t('public.experiences.units.per_session')
}
function transferFromPrice() {
  return props.item.from_price ? `${t('public.transfers.from')} ${props.item.from_price}` : ''
}

// Trust line: vehicles carry the operator's blanket free-cancellation
// promise; tours surface their per-tour policy only when it's free.
function trustLine() {
  if (props.kind === 'vehicle') return t('public.trust.free_cancellation_vehicle')
  if (props.kind === 'tour' && props.item.cancellation_policy === 'free' && props.item.cancellation_cutoff_hours != null) {
    return t('public.tours.cancellation.free', { hours: props.item.cancellation_cutoff_hours })
  }
  return ''
}
</script>

<template>
  <a :href="href" class="pcard">
    <div class="pcard__photo">
      <span v-if="item.verified" class="pcard__verified">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
          <path d="M12 3l7 3v5c0 4.5-3 7.6-7 9-4-1.4-7-4.5-7-9V6z" /><path d="M9 12l2 2 4-4" />
        </svg>{{ t('public.verified') }}
      </span>
      <span v-if="kind === 'tour' && item.featured" class="pcard__featured">★ {{ t('public.tours.badges.featured') }}</span>
      <span v-if="item.luxury" class="pcard__vip" :class="{ 'pcard__vip--stacked': item.verified }">{{ t('public.vip.badge') }}</span>
      <span v-if="item.unclaimed" class="pcard__unclaimed">{{ t('public.unclaimed.badge') }}</span>
      <template v-if="item.photo_url">
        <div class="pcard__backdrop" :style="{ backgroundImage: `url(${item.photo_url})` }" aria-hidden="true"></div>
        <img :src="item.photo_url" :alt="title()" loading="lazy" decoding="async">
      </template>
      <CatalogPlaceholder v-else :kind="kind" :category="item.category" />
    </div>
    <div class="pcard__body">
      <h3 class="pcard__title">{{ title() }}</h3>
      <span v-if="item.tier === 'premium' && !item.luxury" class="pcard__tier">{{ t('public.tiers.premium') }}</span>
      <span v-if="kind === 'tour' && item.tour_type && item.tour_type !== 'group' && !(item.tour_type === 'vip' && item.luxury)" class="pcard__type">{{ t(`public.tours.badges.${item.tour_type}`) }}</span>
      <ul v-if="kind === 'tour' && item.vibes?.length" class="pcard__vibes">
        <li v-for="v in item.vibes" :key="v">{{ t(`public.tours.vibes.${v}`) }}</li>
      </ul>
      <p v-if="meta()" class="pcard__meta">{{ meta() }}</p>
      <p v-if="(kind === 'property' || kind === 'charter') && item.features?.length" class="pcard__guides">
        {{ item.features.slice(0, 3).map(f => t(`public.${kind === 'charter' ? 'charters' : 'properties'}.features.items.${f}`)).join(' · ') }}
      </p>
      <p v-if="kind === 'tour' && item.guide_languages?.length" class="pcard__guides">
        {{ item.guide_languages.map(code => t(`public.experiences.guide_languages.short.${code}`)).join(' · ') }}
      </p>
      <p v-if="trustLine()" class="pcard__trust">✓ {{ trustLine() }}</p>
      <p class="pcard__price">
        <template v-if="item.price_on_request">
          <span class="fw-value">{{ t('public.vip.price_on_request') }}</span>
        </template>
        <template v-else>
          <span v-if="item.unclaimed" class="pcard__from">{{ t('public.unclaimed.from') }} </span>
          <span class="fw-value">{{ price() }}</span>
          <span v-if="kind === 'vehicle'" class="pcard__per"> {{ t('public.catalog.per_day') }}</span>
          <span v-else-if="kind === 'tour'" class="pcard__per"> {{ tourPriceUnit() }}</span>
          <span v-else-if="kind === 'charter'" class="pcard__per"> {{ t('public.charters.per_boat') }}</span>
          <span v-else-if="item.price_per_night || item.price_per_month" class="pcard__per"> {{ item.price_per_night ? t('public.catalog.per_night') : t('public.catalog.per_month') }}</span>
        </template>
      </p>
    </div>
  </a>
</template>

<style scoped>
.pcard__tier { display: inline-block; margin: 0 0 0.35rem; padding: 0.15rem 0.55rem; border-radius: 999px; font-size: 0.7rem; font-weight: 700; letter-spacing: 0.06em; text-transform: uppercase; background: var(--infoSoft, var(--secondSoft)); color: var(--info, var(--second)); }
.pcard {
  display: block; width: 100%;
  background: var(--surface); border: 1px solid var(--line);
  border-radius: 1rem; overflow: hidden; text-decoration: none; color: inherit;
  transition: transform 160ms ease, border-color 160ms ease, box-shadow 160ms ease;
}
.pcard:hover {
  transform: translateY(-3px);
  border-color: color-mix(in oklch, var(--card-accent, var(--accent)) 55%, var(--line));
  box-shadow: 0 16px 36px -22px color-mix(in oklch, var(--card-accent, var(--accent)) 70%, transparent);
}
.pcard__photo { position: relative; aspect-ratio: 4 / 3; background: var(--surfaceAlt); display: grid; place-items: center; overflow: hidden; }
.pcard__verified {
  position: absolute; top: 0.6rem; left: 0.6rem; z-index: 1;
  display: inline-flex; align-items: center; gap: 0.3rem;
  padding: 0.3rem 0.6rem 0.3rem 0.5rem; border-radius: 999px;
  background: var(--surface); color: var(--ink);
  font-size: 0.78rem; font-weight: 700; line-height: 1;
  box-shadow: 0 4px 12px -4px color-mix(in oklch, var(--ink) 40%, transparent);
}
.pcard__verified svg { width: 0.95rem; height: 0.95rem; color: var(--ok); }
/* Whole photo (contain) over a blurred copy of itself — so tall portrait
   phone shots show the full vehicle instead of being zoom-cropped, matching
   the detail-page gallery. */
.pcard__backdrop {
  position: absolute; inset: 0; z-index: 0;
  background-size: cover; background-position: center;
  filter: blur(18px) brightness(0.85); transform: scale(1.15);
}
/* z-index 0 (not 1): above the backdrop (earlier in DOM) but below every
   badge (z-index 1+), which are painted earlier in DOM and must stay on top. */
.pcard__photo img { position: relative; z-index: 0; width: 100%; height: 100%; object-fit: contain; }
.pcard__body { padding: 0.9rem 1rem 1.05rem; }
.pcard__title { margin: 0 0 0.3rem; font-size: 1.05rem; font-weight: 700; color: var(--ink); }
.pcard__meta { margin: 0 0 0.5rem; font-size: 0.9rem; color: var(--inkSoft); }
.pcard__trust { margin: 0 0 0.45rem; font-size: 0.8rem; font-weight: 600; color: var(--ok); }
.pcard__price { margin: 0; font-size: 1.15rem; color: var(--ink); }
.pcard__per { font-size: 0.85rem; color: var(--muted); }
.pcard__featured {
  position: absolute; top: 0.6rem; right: 0.6rem; z-index: 1;
  padding: 0.3rem 0.6rem; border-radius: 999px;
  background: var(--accent); color: var(--accentInk);
  font-size: 0.78rem; font-weight: 800; line-height: 1;
  box-shadow: 0 4px 12px -4px color-mix(in oklch, var(--ink) 40%, transparent);
}
.pcard__type {
  display: inline-block; margin: 0 0 0.4rem; padding: 0.15rem 0.55rem;
  border-radius: 999px; font-size: 0.74rem; font-weight: 700;
  background: color-mix(in oklch, var(--card-accent, var(--accent)) 14%, var(--surface));
  color: var(--card-accent, var(--accent));
}
.pcard__vibes { list-style: none; margin: 0 0 0.5rem; padding: 0; display: flex; flex-wrap: wrap; gap: 0.3rem; }
.pcard__vibes li {
  font-size: 0.72rem; color: var(--inkSoft);
  background: var(--surfaceAlt); border: 1px solid var(--line);
  border-radius: 999px; padding: 0.12rem 0.5rem;
}
.pcard__vip {
  position: absolute; top: 0.6rem; left: 0.6rem; z-index: 2;
  padding: 0.2rem 0.6rem; border-radius: 999px; font-size: 0.72rem; font-weight: 700;
  background: var(--ink); color: var(--warn); border: 1px solid var(--warn);
}
/* When a card is BOTH verified and VIP, drop the VIP pill below the verified
   pill (top-left, ~1.9rem tall) so the two never overlap. VIP alone stays in
   the top-left corner. */
.pcard__vip--stacked { top: 2.6rem; }
.pcard__unclaimed {
  position: absolute; top: 0.6rem; left: 0.6rem; z-index: 1;
  padding: 0.2rem 0.6rem; border-radius: 999px; font-size: 0.72rem; font-weight: 700;
  background: var(--surfaceAlt); color: var(--muted); border: 1px solid var(--line);
}
.pcard__from { font-size: 0.85rem; color: var(--muted); }
.pcard__guides {
  margin: 0 0 0.45rem; font-size: 0.78rem; font-weight: 600; color: var(--inkSoft);
  letter-spacing: 0.02em;
}
</style>
